import { type FC, useState } from 'react';
import { Button, Group, Loader, Paper, Select, Stack, Text, TextInput } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { defaultOptionsFilter } from '~/shared/lib/utils/defaultOptionsFilter';
import { OrderedEntryList, type OrderedEntry } from '~/shared/ui/OrderedEntryList';
import { type TemplateGroupEntryValue } from '../lib';

interface CascadeTemplateGroupsCardProps {
    name: string;
    onNameChange: (value: string) => void;
    entries: OrderedEntry<TemplateGroupEntryValue>[];
    onEntriesChange: (entries: OrderedEntry<TemplateGroupEntryValue>[]) => void;
    groupOptions: { value: string; label: string }[];
    isGroupOptionsLoading: boolean;
    onAddGroup: (groupId: string) => void;
    onClear: () => void;
    isValid: boolean;
    isSubmitPending: boolean;
    onSubmit: () => void;
}

export const CascadeTemplateGroupsCard: FC<CascadeTemplateGroupsCardProps> = ({
    name,
    onNameChange,
    entries,
    onEntriesChange,
    groupOptions,
    isGroupOptionsLoading,
    onAddGroup,
    onClear,
    isValid,
    isSubmitPending,
    onSubmit,
}) => {
    const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

    const handleAddClick = () => {
        if (!selectedGroupId) {
            return;
        }

        onAddGroup(selectedGroupId);
        setSelectedGroupId(null);
    };

    return (
        <Paper
            p='md'
            radius='md'
            withBorder
        >
            <Stack gap='md'>
                <TextInput
                    label='Название'
                    placeholder='Введите название'
                    value={name}
                    onChange={(event) => onNameChange(event.currentTarget.value)}
                />

                <Stack gap='xs'>
                    <Text
                        fw={600}
                        size='sm'
                    >
                        Группы
                    </Text>

                    <Group
                        align='flex-end'
                        gap='sm'
                    >
                        <Select
                            flex={1}
                            miw={240}
                            searchable
                            clearable
                            placeholder='Выберите группу'
                            data={groupOptions}
                            value={selectedGroupId}
                            onChange={setSelectedGroupId}
                            filter={defaultOptionsFilter}
                            disabled={isGroupOptionsLoading}
                            leftSection={isGroupOptionsLoading ? <Loader size='xs' /> : null}
                        />

                        <Button
                            leftSection={<IconPlus size={16} />}
                            disabled={!selectedGroupId}
                            onClick={handleAddClick}
                        >
                            Добавить
                        </Button>

                        <Button
                            color='red'
                            variant='outline'
                            leftSection={<IconTrash size={16} />}
                            disabled={entries.length === 0}
                            onClick={onClear}
                        >
                            Очистить
                        </Button>
                    </Group>

                    <OrderedEntryList
                        entries={entries}
                        onChange={onEntriesChange}
                        emptyMessage='Группы не выбраны'
                        renderEntry={(value) => (
                            <div>
                                <Text size='sm'>{value.name}</Text>
                                <Text
                                    size='xs'
                                    c='dimmed'
                                >
                                    {value.id}
                                </Text>
                            </div>
                        )}
                    />
                </Stack>

                <Button
                    type='button'
                    disabled={!isValid || isSubmitPending}
                    loading={isSubmitPending}
                    onClick={onSubmit}
                >
                    Сохранить
                </Button>
            </Stack>
        </Paper>
    );
};
